// Simular antes de firmar. Nómina → Contratos.
// Responde la pregunta que siempre se hace y que el sistema sólo sabía
// contestar hacia atrás: cuánto cuesta DE VERDAD contratar a esta persona.
// Entre «le pago 600» y «me cuesta 600» hay cerca de un tercio.
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Calculator, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { Campo, Modal, fieldCls, money, nomRpc, type CostoSimulacion } from './shared';

interface Fila { nombre: string; sueldo: string; horas: string; cargas: string; ingreso: string }

const vacia = (n: number): Fila => ({
  nombre: n === 1 ? 'Jornada completa' : `Escenario ${n}`,
  sueldo: '', horas: '40', cargas: '0', ingreso: '',
});

export function SimuladorCostoModal({ onClose }: { onClose: () => void }) {
  const [filas, setFilas] = useState<Fila[]>([vacia(1)]);
  const [res, setRes] = useState<CostoSimulacion | null>(null);

  const simular = useMutation({
    mutationFn: () => nomRpc<CostoSimulacion>('nom_costo_simular', {
      _data: {
        escenarios: filas.map((f) => ({
          nombre: f.nombre,
          sueldo: Number(f.sueldo) || 0,
          horas_semanales: Number(f.horas) || 40,
          cargas_familiares: Number(f.cargas) || 0,
          // Sin fecha de ingreso previa se asume que entra ahora: los fondos
          // de reserva no corren el primer año (Art. 196) y eso cambia mucho.
          fecha_ingreso: f.ingreso || null,
        })),
      },
    }),
    onSuccess: setRes,
    onError: (e) => { setRes(null); toast.error(getErrorMessage(e)); },
  });

  const set = (i: number, p: Partial<Fila>) =>
    setFilas(filas.map((f, j) => (j === i ? { ...f, ...p } : f)));

  const esc = res?.escenarios ?? [];

  return (
    <Modal title="¿Cuánto cuesta contratar a esta persona?" onClose={onClose} wide>
      <div className="space-y-4">
        <div className="space-y-2">
          {filas.map((f, i) => (
            <div key={i} className="grid grid-cols-2 sm:grid-cols-6 gap-2 items-end">
              <Campo label="Escenario"><input className={`${fieldCls} h-9`} value={f.nombre}
                onChange={(e) => set(i, { nombre: e.target.value })} /></Campo>
              <Campo label="Sueldo mensual"><input type="number" step="0.01" className={`${fieldCls} h-9`} value={f.sueldo}
                onChange={(e) => set(i, { sueldo: e.target.value })} /></Campo>
              <Campo label="Horas/semana"><input type="number" className={`${fieldCls} h-9`} value={f.horas}
                onChange={(e) => set(i, { horas: e.target.value })} /></Campo>
              <Campo label="Cargas familiares"><input type="number" className={`${fieldCls} h-9`} value={f.cargas}
                onChange={(e) => set(i, { cargas: e.target.value })} /></Campo>
              <Campo label="Ya trabaja desde"><input type="date" className={`${fieldCls} h-9`} value={f.ingreso}
                onChange={(e) => set(i, { ingreso: e.target.value })} /></Campo>
              <div>
                {filas.length > 1 && (
                  <button type="button" className="text-destructive h-9" aria-label="Quitar escenario"
                    onClick={() => setFilas(filas.filter((_, j) => j !== i))}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          ))}
          <div className="flex gap-2">
            {filas.length < 4 && (
              <Button size="sm" variant="outline" onClick={() => setFilas([...filas, vacia(filas.length + 1)])}>
                <Plus className="w-4 h-4 mr-1" /> Comparar con otro
              </Button>
            )}
            <Button size="sm" isLoading={simular.isPending}
              disabled={filas.every((f) => !Number(f.sueldo))} onClick={() => simular.mutate()}>
              <Calculator className="w-4 h-4 mr-1" /> Calcular
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            «Ya trabaja desde» sólo hace falta si la persona <b>ya está</b> en la empresa: los fondos de reserva
            corren desde el mes 13 de servicio (Art. 196), así que en una contratación nueva no se pagan el primer año.
          </p>
        </div>

        {esc.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-2 pr-3">Concepto</th>
                {esc.map((e) => <th key={e.nombre} className="py-2 pr-3 text-right">{e.nombre}</th>)}
              </tr></thead>
              <tbody>
                {([
                  ['Sueldos del año', 'sueldos'],
                  ['Décimo tercero (Art. 111)', 'decimo_tercero'],
                  ['Décimo cuarto (Art. 113)', 'decimo_cuarto'],
                  ['Fondos de reserva (Art. 196)', 'fondos_reserva'],
                  ['Vacaciones (Art. 69)', 'vacaciones'],
                  ['Aporte patronal', 'aporte_patronal'],
                  ['IECE', 'iece'],
                  ['SECAP', 'secap'],
                ] as const).map(([lab, k]) => (
                  <tr key={k} className="border-b last:border-0">
                    <td className="py-1.5 pr-3 text-muted-foreground">{lab}</td>
                    {esc.map((e) => (
                      <td key={e.nombre} className="py-1.5 pr-3 text-right font-mono tabular-nums">
                        {money(e[k] as number)}
                      </td>
                    ))}
                  </tr>
                ))}
                <tr className="border-b font-semibold bg-muted/40">
                  <td className="py-2 pr-3">Le cuesta a la empresa (año)</td>
                  {esc.map((e) => (
                    <td key={e.nombre} className="py-2 pr-3 text-right font-mono tabular-nums">{money(e.costo_anual)}</td>
                  ))}
                </tr>
                <tr className="border-b">
                  <td className="py-1.5 pr-3 text-muted-foreground">Al mes</td>
                  {esc.map((e) => (
                    <td key={e.nombre} className="py-1.5 pr-3 text-right font-mono tabular-nums">{money(e.costo_mensual)}</td>
                  ))}
                </tr>
                <tr className="border-b">
                  <td className="py-1.5 pr-3 text-muted-foreground">Por encima del sueldo</td>
                  {esc.map((e) => (
                    <td key={e.nombre} className="py-1.5 pr-3 text-right font-mono tabular-nums">
                      {Number(e.sobre_sueldo_pct).toFixed(1)} %
                    </td>
                  ))}
                </tr>
                <tr className="border-b font-semibold">
                  <td className="py-2 pr-3">La persona recibe (año)</td>
                  {esc.map((e) => (
                    <td key={e.nombre} className="py-2 pr-3 text-right font-mono tabular-nums text-ok">{money(e.neto_anual)}</td>
                  ))}
                </tr>
                <tr className="border-b">
                  <td className="py-1.5 pr-3 text-muted-foreground">Al mes, en la mano</td>
                  {esc.map((e) => (
                    <td key={e.nombre} className="py-1.5 pr-3 text-right font-mono tabular-nums">{money(e.neto_mensual)}</td>
                  ))}
                </tr>
                <tr>
                  <td className="py-1.5 pr-3 text-muted-foreground">Por cada dólar que recibe, la empresa pone</td>
                  {esc.map((e) => (
                    <td key={e.nombre} className="py-1.5 pr-3 text-right font-mono tabular-nums font-semibold">
                      {Number(e.por_dolar_neto).toFixed(2)}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
            {esc.length > 1 && res && (
              <p className="text-xs text-muted-foreground mt-2">
                El más barato es <b>{res.mas_barato}</b>; entre el más caro y el más barato hay {money(res.diferencia)} al año.
              </p>
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}
